// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-23) — where a dataset's raw data came from: the
 * `platforms` block of DSA 1.10 (`schema/dsa-v1.10-notes.md`), read
 * leniently, and the part of it the Satellites layer acts on: the
 * satellites (NORAD ids) whose validity window holds the frame's time.
 * Those pulse on the globe while the dataset is up and the layer is on,
 * so a viewer sees which satellites imaged the picture (GeoColor: GOES-19,
 * GOES-18, Himawari-9, Meteosat-12, Meteosat-9).
 *
 * The other kinds (ground stations, buoys, ships, aircraft, drones,
 * radars, model runs) are kept as read for the day a player draws them;
 * nothing here places them.
 */

export type PlatformKind =
  | 'satellite'
  | 'ground-station'
  | 'buoy'
  | 'ship'
  | 'aircraft'
  | 'drone'
  | 'balloon'
  | 'radar'
  | 'sensor-network'
  | 'model'
  | 'other'

export const PLATFORM_KINDS: readonly PlatformKind[] = [
  'satellite',
  'ground-station',
  'buoy',
  'ship',
  'aircraft',
  'drone',
  'balloon',
  'radar',
  'sensor-network',
  'model',
  'other',
]

/**
 * `fallback` (VPS, 2026-10-24): stands in when the primary is missing
 * (VIIRS: NOAA-21 primary, NOAA-20 fallback). Drawn only when no primary
 * or contributing entry of the same layers is active at that instant;
 * listed dimmed otherwise.
 */
export type PlatformRole = 'primary' | 'contributing' | 'fallback' | 'calibration' | 'relay'
const ROLES: readonly PlatformRole[] = ['primary', 'contributing', 'fallback', 'calibration', 'relay']

/** One entry of `platforms[]`, as the app keeps it (names resolved later, per language). */
export interface DsaPlatform {
  readonly id: string
  readonly kind: PlatformKind
  /** A plain string or `{ en, es, ca… }`, as written. */
  readonly name: string | Readonly<Record<string, string>>
  readonly operator: string | null
  readonly instrument: string | null
  readonly role: PlatformRole
  /** NORAD catalogue number; null for anything but a satellite. */
  readonly norad: number | null
  readonly cospar: string | null
  readonly nominalLongitude: number | null
  readonly location: { lon: number; lat: number; elevationM: number | null; altitudeKm: number | null } | null
  /** Validity window, epoch ms; null = open at that end. */
  readonly fromMs: number | null
  readonly toMs: number | null
  /** `layers[].order` values it feeds; empty = every data layer. */
  readonly layers: readonly number[]
  readonly source: string | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function dateMs(value: unknown): number | null {
  const s = str(value)
  if (!s) return null
  const ms = Date.parse(s)
  return Number.isFinite(ms) ? ms : null
}

const ID = /^[a-z0-9][a-z0-9-]{0,63}$/

/**
 * The `platforms` block of a descriptor, or an empty list when it has
 * none. Lenient: an entry without its required fields (id, kind, name;
 * a NORAD id for a satellite) is dropped and the rest kept, so one bad
 * line never hides the others. Never throws.
 */
export function parsePlatforms(raw: unknown): DsaPlatform[] {
  const dsa = asRecord(raw)
  if (!dsa || !Array.isArray(dsa.platforms)) return []
  const out: DsaPlatform[] = []
  const seen = new Set<string>()
  for (const entry of dsa.platforms) {
    const p = asRecord(entry)
    if (!p) continue
    const id = str(p.id)
    const kind = str(p.kind) as PlatformKind | null
    if (!id || !ID.test(id) || seen.has(id) || !kind || !PLATFORM_KINDS.includes(kind)) continue
    const nameText = str(p.name)
    const nameRecord = asRecord(p.name)
    const name: DsaPlatform['name'] | null = nameText
      ? nameText
      : nameRecord && Object.values(nameRecord).some((v) => str(v))
        ? Object.fromEntries(Object.entries(nameRecord).flatMap(([k, v]) => (str(v) ? [[k, str(v)!]] : [])))
        : null
    if (!name) continue
    const sat = asRecord(p.satellite)
    const norad = sat ? num(sat.norad) : null
    if (kind === 'satellite' && (norad === null || !Number.isInteger(norad) || norad < 1)) continue
    const loc = asRecord(p.location)
    const lon = loc ? num(loc.lon) : null
    const lat = loc ? num(loc.lat) : null
    const role = str(p.role) as PlatformRole | null
    const fromMs = dateMs(p.from)
    const toMs = dateMs(p.to)
    seen.add(id)
    out.push({
      id,
      kind,
      name,
      operator: str(p.operator),
      instrument: str(p.instrument),
      role: role && ROLES.includes(role) ? role : 'primary',
      norad: kind === 'satellite' ? norad : null,
      cospar: sat ? str(sat.cospar) : null,
      nominalLongitude: sat ? num(sat.nominalLongitude) : null,
      location: kind !== 'satellite' && lon !== null && lat !== null ? { lon, lat, elevationM: num(loc?.elevationM), altitudeKm: num(loc?.altitudeKm) } : null,
      fromMs,
      toMs: fromMs !== null && toMs !== null && toMs <= fromMs ? null : toMs,
      layers: Array.isArray(p.layers) ? p.layers.filter((n): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1) : [],
      source: str(p.source),
    })
  }
  return out
}

/** The entry's name in a language, English when it has none, then whatever it has. */
export function platformName(p: Pick<DsaPlatform, 'name'>, lang: string): string {
  if (typeof p.name === 'string') return p.name
  const base = lang.toLowerCase().split('-')[0] ?? 'en'
  return p.name[base] ?? p.name.en ?? Object.values(p.name)[0] ?? ''
}

/** Does the platform's window hold this instant? An open end holds everything; no time holds every entry. */
export function platformActiveAt(p: Pick<DsaPlatform, 'fromMs' | 'toMs'>, atMs: number | null): boolean {
  if (atMs === null) return true
  if (p.fromMs !== null && atMs < p.fromMs) return false
  if (p.toMs !== null && atMs >= p.toMs) return false
  return true
}

/** A satellite that supplies the data at an instant, or a stand-in held back while its primary is active. */
export interface SourceAt {
  readonly platform: DsaPlatform
  /** True for a `fallback` entry whose primary or contributing entry (same layers) is active: listed dimmed, not drawn. */
  readonly standby: boolean
}

/** Do two entries feed a common layer? (Empty = every data layer.) */
function shareLayers(a: DsaPlatform, b: DsaPlatform): boolean {
  if (!a.layers.length || !b.layers.length) return true
  return a.layers.some((n) => b.layers.includes(n))
}

/**
 * The satellites among the platforms whose window holds this instant, in
 * the order written. A `fallback` entry is a stand-in: it counts as
 * drawn only when no primary or contributing satellite of the same
 * layers is active then (`standby: false`), and is held back otherwise
 * (`standby: true`). Calibration and relay entries are never drawn.
 */
export function sourcesAt(platforms: readonly DsaPlatform[], atMs: number | null): SourceAt[] {
  const active = platforms.filter((p) => p.kind === 'satellite' && p.norad !== null && platformActiveAt(p, atMs))
  const leads = active.filter((p) => p.role === 'primary' || p.role === 'contributing')
  const out: SourceAt[] = []
  for (const p of active) {
    if (p.role === 'primary' || p.role === 'contributing') out.push({ platform: p, standby: false })
    else if (p.role === 'fallback') out.push({ platform: p, standby: leads.some((lead) => shareLayers(lead, p)) })
  }
  return out
}

/** The satellites drawn at this instant (no stand-ins held back), in the order written. */
export function activeSatellites(platforms: readonly DsaPlatform[], atMs: number | null): DsaPlatform[] {
  return sourcesAt(platforms, atMs)
    .filter((s) => !s.standby)
    .map((s) => s.platform)
}
